/**
 * 통합 카카오 지도 컴포넌트 (반응형)
 * 카카오맵 API를 사용하여 지정된 주소의 지도를 표시합니다.
 * 데스크톱과 모바일 모두에서 사용 가능한 반응형 컴포넌트입니다.
 */

import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

const DEFAULT_COORDS = { lat: 37.565, lng: 127.0176 };

const Map = ({ address }) => {
  const containerRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // ✅ react-snap / SSR 차단
    if (typeof window === 'undefined') return;
    if (navigator.userAgent === 'ReactSnap') return;

    // ✅ 이미 로드되어 있으면 바로 실행
    if (window.kakao && window.kakao.maps) {
      initMap();
      return;
    }

    // ✅ SDK 동적 로딩
    const script = document.createElement('script');
    script.src = `https://dapi.kakao.com/v2/maps/sdk.js?appkey=${process.env.REACT_APP_KAKAO_MAP_KEY}&libraries=services&autoload=false`;
    script.async = true;

    script.onload = () => {
      window.kakao.maps.load(initMap);
    };

    document.head.appendChild(script);

    /**
     * 카카오맵 Geocoder가 인식할 수 있도록 주소를 정제하는 함수
     * @param {string} address - 원본 주소
     * @returns {string} - 정제된 주소
     */
    function cleanAddressForGeocoding(address) {
      if (!address) return address;

      let cleaned = address;

      // 1. 우편번호 제거: (우)12345 형태 제거
      cleaned = cleaned.replace(/\(우\)\d{5}\s*/g, '');

      // 2. 층수 정보 제거: , N층 형태 제거
      cleaned = cleaned.replace(/,\s*\d+층/g, '');

      // 3. 도로명+번호 사이 띄어쓰기 추가 (난계로185 → 난계로 185)
      cleaned = cleaned.replace(/([가-힣]+로)(\d+)/g, '$1 $2');

      return cleaned.trim();
    }

    function initMap() {
      const kakao = window.kakao;
      const container = containerRef.current;
      if (!container) return;

      const createMap = (lat, lng) => {
        const map = new kakao.maps.Map(container, {
          center: new kakao.maps.LatLng(lat, lng),
          level: 2,
        });

        const marker = new kakao.maps.Marker({
          position: map.getCenter(),
        });
        marker.setMap(map);

        new kakao.maps.InfoWindow({
          content: `<div style="width: 200px; padding: 6px 0; text-align: center;">에스이임파워사회적협동조합<br/>중구행복돌봄센터</div>`,
        }).open(map, marker);

        setIsLoading(false);
      };

      if (address) {
        const geocoder = new kakao.maps.services.Geocoder();
        const cleanedAddress = cleanAddressForGeocoding(address);

        // 개발 환경에서만 디버그 로그 출력
        if (process.env.NODE_ENV === 'development') {
          console.log('🔍 [카카오맵] 원본 주소:', address);
          console.log('🔍 [카카오맵] 정제된 주소:', cleanedAddress);
        }

        geocoder.addressSearch(cleanedAddress, (result, status) => {
          if (process.env.NODE_ENV === 'development') {
            console.log('📍 [카카오맵] 지오코딩 상태:', status);
            console.log('📍 [카카오맵] 지오코딩 결과:', result);
          }

          if (status === kakao.maps.services.Status.OK) {
            if (process.env.NODE_ENV === 'development') {
              console.log('✅ [카카오맵] 좌표 변환 성공 - 위도:', result[0].y, '경도:', result[0].x);
            }
            createMap(result[0].y, result[0].x);
          } else {
            console.warn('❌ [카카오맵] 좌표 변환 실패 - 기본 좌표 사용');
            createMap(DEFAULT_COORDS.lat, DEFAULT_COORDS.lng);
          }
        });
      } else {
        console.warn('⚠️ [카카오맵] 주소 정보 없음 - 기본 좌표 사용');
        createMap(DEFAULT_COORDS.lat, DEFAULT_COORDS.lng);
      }
    }
  }, [address]);

  return (
    <Container data-react-snap="skip">
      <MapContainer ref={containerRef} />
      {isLoading && <LoadingText>지도를 불러오는 중입니다...</LoadingText>}
    </Container>
  );
};

export default Map;

/* Styled Components */

const Container = styled.div`
  width: 100%;
  box-sizing: border-box;
  margin: auto;

  /* 모바일: 상단 여백 */
  margin-top: 30px;

  /* 태블릿 이상: 여백 조정 */
  @media ${(props) => props.theme.media.tablet} {
    margin-top: 50px;
  }
`;

const MapContainer = styled.div`
  width: 100%;

  /* 모바일: 250px */
  height: 250px;

  /* 태블릿: 350px */
  @media ${(props) => props.theme.media.tablet} {
    height: 350px;
  }

  /* 큰 데스크톱: 400px */
  @media ${(props) => props.theme.media.desktop} {
    height: 400px;
  }

  /* 지도 외곽선 (선택사항) */
  border: 1px solid ${(props) => props.theme.colors.borderLight};
  border-radius: 4px;
  overflow: hidden;
`;

const LoadingText = styled.p`
  text-align: center;
  margin-top: ${(props) => props.theme.spacing.md};
  color: ${(props) => props.theme.colors.textSecondary};
  font-size: ${(props) => props.theme.fontSizes.sm};
`;
